import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import PhoneInput from '../shared/PhoneInput';
import Input from '../shared/Input';
import Drawer from '../shared/Drawer';
import Select from '../shared/Select';
import Autocomplete from '../shared/Autocomplete';

import userRoles, { distributorRoles, countryRoles, companyRoles } from '../../constants/userRoles';

const engineerOptions = [
  { label: 'Level 1 – Senior Engineer', value: 1 },
  { label: 'Level 2 – Mid-Level Engineer', value: 2 },
  { label: 'Level 3 – Entry-Level Engineer', value: 3 }
];

const isAssignableRole = (key, currentUserRole) => (
  key !== userRoles.GLOBAL_ADMIN.name && (
    currentUserRole === userRoles.GLOBAL_ADMIN.name ||
    companyRoles.includes(key) ||
    key === userRoles.SURGEON.name
  )
);

const isGlobalAdminUser = (user) => user?.role === userRoles.GLOBAL_ADMIN.name;

const getUniqueValues = (values = []) => Array.from(new Set(values.filter(Boolean)));

const EditUserModal = (props) => {
  const {
    open,
    onClose,
    user,
    onSubmit,
    loading,
    distributorsList = [],
    // countriesList,
    manufacturers = [],
    logisticsTeams = [],
    radiologyTeams = [],
    companies = [],
    teams = [],
    currentUser,
  } = props;

  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [address, setAddress] = useState(user?.address || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [password, setPassword] = useState('');

  const [role, setRole] = useState('');
  const [company, setCompany] = useState(null);
  const [administrationCountries, setAdministrationCountries] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [, setManufacturer] = useState(null);
  const [, setLogistics] = useState(null);
  const [, setRadiology] = useState(null);
  const [engineerLevel, setEngineerLevel] = useState(1);

  const visibleDistributorIds = useMemo(() => (
    getUniqueValues(distributorsList.map((distributor) => distributor?.id))
  ), [distributorsList]);

  const hiddenDistributorIds = useMemo(() => {
    if (currentUser?.role !== userRoles.COUNTRY_ADMIN.name) {
      return [];
    }

    return getUniqueValues(user?.distributors || [])
      .filter((distributorId) => !visibleDistributorIds.includes(distributorId));
  }, [currentUser?.role, user, visibleDistributorIds]);

  const hiddenAdministrationCountries = useMemo(() => {
    if (currentUser?.role !== userRoles.COUNTRY_ADMIN.name || !hiddenDistributorIds.length) {
      return [];
    }

    return getUniqueValues(user?.administrationCountries || [])
      .filter((country) => !currentUser?.administrationCountries?.includes(country));
  }, [currentUser, user, hiddenDistributorIds]);

  useEffect(() => {
    if (open && user) {
      setFirstName(user?.firstName || '');
      setLastName(user?.lastName || '');
      setAddress(user?.address || '');
      setPhone(user?.phone || '');
      setPassword('');
      setRole(user?.role || '');
      setCompany(user?.company ? companies.find((c) => c.id === user.company) : null);
      setDistributors(user.distributors
        ? user.distributors?.map((id) => distributorsList.find((d) => d.id === id)).filter(Boolean)
        : []);
      setAdministrationCountries(user.administrationCountries || []);
      setManufacturer(user.manufacturer ? manufacturers.find((m) => m.id === user.manufacturer) : null);
      setLogistics(user.logistics ? logisticsTeams.find((l) => l.id === user.logistics) : null);
      setRadiology(user.radiology ? radiologyTeams.find((r) => r.id === user.radiology) : null);
      setEngineerLevel(user?.engineerLevel || 1);
    }
  }, [open, user, distributorsList, manufacturers, radiologyTeams, companies, logisticsTeams])

  const rolesOptions = useMemo(() => (
    Object.keys(userRoles)
      .filter((key) => isAssignableRole(key, currentUser.role))
      .map((key) => ({
        label: userRoles[key]?.label,
        value: userRoles[key]?.name,
      }))
  ), [currentUser]);

  const resetValues = () => {
    setFirstName(firstName || '');
    setLastName(lastName || '');
    setAddress(address || '');
    setPhone(phone || '');
    setPassword('');
    setRole('');
    setCompany(null);
    setDistributors([]);
    setAdministrationCountries([]);
    setManufacturer(null);
    setRadiology(null);
    setEngineerLevel(null);
    setLogistics(null);
  };

  const handleSubmit = async () => {
    const formObj = {
      firstName: firstName || '',
      lastName: lastName || '',
      address: address || '',
      phone: phone || '',
      password: password || '',
      role: role || '',
    };

    if (companyRoles.includes(role)) {
      formObj.company = company?.id;
    }

    if (distributorRoles.includes(role) || role === userRoles.SURGEON.name) {
      formObj.distributors = getUniqueValues([
        ...hiddenDistributorIds,
        ...(distributors || []).map((d) => d?.id),
      ]);
      const countries = [...hiddenAdministrationCountries];
      distributors.forEach((d) => {
        if (d?.country && !countries?.includes(d.country)) {
          countries.push(d.country);
        }
      })
      formObj.administrationCountries = countries;
    }

    if (countryRoles.includes(role)) {
      formObj.administrationCountries = administrationCountries || [];
      formObj.distributors = [];
    }

    // if (role === userRoles.MANUFACTURER.name) {
    //   formObj.manufacturer = manufacturer?.id || '';
    // } else {
    //   formObj.manufacturer = null;
    // }
    //
    // if (role === userRoles.LOGISTICS.name) {
    //   formObj.logistics = logistics?.id || '';
    // } else {
    //   formObj.logistics = null;
    // }
    //
    // if (role === userRoles.RADIOLOGY.name) {
    //   formObj.radiology = radiology?.id || '';
    // } else {
    //   formObj.radiology = null;
    // }

    if (role === userRoles.ENGINEER.name) {
      formObj.engineerLevel = engineerLevel || 1;
    }

    if ([userRoles.GLOBAL_ADMIN.name, userRoles.COUNTRY_ADMIN.name, userRoles.ENGINEER.name, userRoles.MANUFACTURER.name, userRoles.RADIOLOGY.name].includes(role)) {
      formObj.distributors = [];
    }

    if ([userRoles.GLOBAL_ADMIN.name, userRoles.ENGINEER.name, userRoles.MANUFACTURER.name, userRoles.LOGISTICS.name].includes(role)) {
      formObj.administrationCountries = [];
    }

    await onSubmit(formObj);
    resetValues();
  }

  const isDisabled = () => {
    let disabled = !firstName?.length || !lastName?.length || !role?.length;

    if (role && companyRoles.includes(role)) {
      disabled = disabled || !company;
    }

    if (role && distributorRoles.includes(role)) {
      disabled = disabled || !distributors?.length;
    }

    if (role && countryRoles.includes(role)) {
      disabled = disabled || !administrationCountries?.length;
    }

    // if (role && role === userRoles.MANUFACTURER.name) {
    //   disabled = disabled || !manufacturer;
    // }
    //
    // if (role && role === userRoles.LOGISTICS.name) {
    //   disabled = disabled || !logistics;
    // }
    //
    // if (role && role === userRoles.RADIOLOGY.name) {
    //   disabled = disabled || !radiology;
    // }

    if (role && role === userRoles.ENGINEER.name) {
      disabled = disabled || !engineerLevel;
    }

    return disabled;
  };

  const distributorsOptions = useMemo(() => {
    if (currentUser.role === userRoles.COUNTRY_ADMIN.name) {
      return company?.id || currentUser?.company ? distributorsList.filter((d) => currentUser?.administrationCountries?.includes(d.country) && d.company === currentUser?.company) : [];
    }

    if (role === userRoles.SURGEON.name) {
      return distributorsList;
    }

    return distributorsList.filter((d) => d.company === company?.id);
  }, [distributorsList, currentUser, company, role]);

  const shouldHideRoleAndPassword = isGlobalAdminUser(user);

  return (
    <Drawer
      open={open}
      onClose={() => {
        resetValues();
        onClose();
      }}
      onSubmit={handleSubmit}
      title="Edit User"
      subtitle="Make changes to the user details"
      submitText="Save Changes"
      disabled={isDisabled() || loading}
      loading={loading}
    >
      <Box sx={{ minWidth: { xs: 'auto', md: '560px' }, maxWidth: { xs: 'auto', md: '560px' } }}>
        <Grid container spacing={0}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <div className="p-r-sm">
              <Input
                id="firstName"
                type="text"
                // placeholder="First name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                label="First Name"
                required
              />
            </div>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <div className="p-l-sm">
              <Input
                id="lastName"
                type="text"
                // placeholder="Last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                label="Last Name"
                required
              />
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={{ xs: 12 }}>
            <div className="p-t-md">
              <Input
                id="address"
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder=""
                label="Address"
              />
            </div>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <div>
              <Box sx={{ display: 'flex', marginBottom: '2px' }}>
                <Typography sx={{ fontSize: '14px' }}>Phone Number</Typography>
              </Box>
              <PhoneInput
                value={phone}
                onChange={setPhone}
              />
            </div>
          </Grid>
          {!shouldHideRoleAndPassword && (
            <Grid size={{ xs: 12 }}>
              <div>
                <Select
                  value={role}
                  onChange={setRole}
                  options={rolesOptions}
                  label='Assigned Role'
                  required
                />
              </div>
            </Grid>
          )}
          {currentUser?.role === userRoles.GLOBAL_ADMIN.name && companyRoles?.includes(role) && (
            <Grid size={{ xs: 12 }}>
              <div>
                <Autocomplete
                  value={company}
                  onChange={(value) => {
                    setCompany(value);
                    setAdministrationCountries([]);
                    setDistributors([]);
                  }}
                  options={companies}
                  label="Company"
                  getOptionLabel={(option) => option.name}
                  required
                />
              </div>
            </Grid>
          )}
          {(distributorRoles?.includes(role) || role === userRoles.SURGEON.name) && (
            <Grid size={{ xs: 12 }}>
              <div>
                <Autocomplete
                  multiple
                  value={distributors}
                  onChange={setDistributors}
                  options={distributorsOptions}
                  label="Distributors"
                  getOptionLabel={(option) => option?.name}
                  required
                />
              </div>
            </Grid>
          )}
          {countryRoles?.includes(role) && (
            <Grid size={{ xs: 12 }}>
              <div>
                <Autocomplete
                  multiple
                  value={administrationCountries}
                  onChange={setAdministrationCountries}
                  options={currentUser?.role === userRoles.COUNTRY_ADMIN.name ? currentUser?.administrationCountries : (company?.countries || [])}
                  label="Administration Countries"
                  required
                />
              </div>
            </Grid>
          )}
          {role === userRoles.ENGINEER.name && (
            <Grid size={{ xs: 6 }}>
              <div>
                <Select
                  value={engineerLevel}
                  onChange={setEngineerLevel}
                  options={engineerOptions}
                  label="Engineer Level"
                  required
                />
              </div>
            </Grid>
          )}
          {/*{role === userRoles.MANUFACTURER.name && (*/}
          {/*  <Grid size={{ xs: 12 }}>*/}
          {/*    <div>*/}
          {/*      <Autocomplete*/}
          {/*        value={manufacturer}*/}
          {/*        onChange={setManufacturer}*/}
          {/*        options={manufacturers}*/}
          {/*        getOptionLabel={(option) => option.name}*/}
          {/*        label="Manufacturer"*/}
          {/*        required*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  </Grid>*/}
          {/*)}*/}
          {/*{role === userRoles.LOGISTICS.name && (*/}
          {/*  <Grid size={{ xs: 12 }}>*/}
          {/*    <div>*/}
          {/*      <Autocomplete*/}
          {/*        value={logistics}*/}
          {/*        onChange={setLogistics}*/}
          {/*        options={logisticsTeams}*/}
          {/*        getOptionLabel={(option) => option.name}*/}
          {/*        label="Logistics Team"*/}
          {/*        required*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  </Grid>*/}
          {/*)}*/}
          {/*{role === userRoles.RADIOLOGY.name && (*/}
          {/*  <Grid size={{ xs: 12 }}>*/}
          {/*    <div>*/}
          {/*      <Autocomplete*/}
          {/*        value={radiology}*/}
          {/*        onChange={setRadiology}*/}
          {/*        options={radiologyTeams}*/}
          {/*        getOptionLabel={(option) => option.name}*/}
          {/*        label="Radiology"*/}
          {/*        required*/}
          {/*      />*/}
          {/*    </div>*/}
          {/*  </Grid>*/}
          {/*)}*/}
          {!shouldHideRoleAndPassword && (
            <Grid size={{ xs: 6 }}>
              <div>
                <Input
                  id="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  label="Password"
                />
              </div>
            </Grid>
          )}

          {role === userRoles.ENGINEER.name && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ mt: 1 }}>
                <Typography fontSize={14} fontWeight={600} sx={{ mb: '6px' }}>Engineering Teams:</Typography>
                {teams?.filter((t) => t.users?.includes(user.uid)).length ? (
                  teams?.filter((t) => t?.users?.includes(user?.uid)).map((team) => (
                    <Box key={team.id} sx={{ mt: '4px', display: 'flex' }}>
                      <Typography fontSize={14}>{team.name}</Typography>
                      <Typography color="secondary" fontSize={14} sx={{ ml: '8px' }}>{team.description}</Typography>
                      {!!team?.countries?.length && (
                        <Typography color="secondary" fontSize={14} sx={{ ml: '4px' }}>{`(${team.countries?.join(', ')})`}</Typography>
                      )}
                    </Box>
                  ))
                ) : (
                  <Typography fontSize={14} color="secondary" mt={1}>No Teams</Typography>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </Drawer>
  );
};

EditUserModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    address: PropTypes.string,
    phone: PropTypes.string,
  }),
  onSubmit: PropTypes.func
};

export default EditUserModal;
